import React from 'react';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Copy, AlertTriangle, Info } from 'lucide-react';

// Small building blocks shared by the DTC Decoder tabs.

export const SectionLabel = ({ children, className = '' }) => (
  <p className={`font-mono text-[11px] uppercase tracking-wider text-muted-foreground ${className}`}>{children}</p>
);

export const CopyButton = ({ getText, testId, label = 'Copy decoded result' }) => {
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(getText());
      toast.success('Decoded result copied to clipboard.');
    } catch {
      toast.error('Could not copy to clipboard.');
    }
  };
  return (
    <Button variant="outline" size="sm" onClick={handleCopy} aria-label={label} data-testid={testId} className="shrink-0">
      <Copy className="h-4 w-4" /> Copy
    </Button>
  );
};

export const WarningList = ({ items, testId }) => (items && items.length ? (
  <div className="space-y-1.5" data-testid={testId}>
    {items.map((w, i) => (
      <div key={i} className="flex items-start gap-2 rounded-md border border-amber-600/30 bg-amber-600/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-400">
        <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
        <span>{w}</span>
      </div>
    ))}
  </div>
) : null);

export const NoteList = ({ items, testId }) => (items && items.length ? (
  <div className="space-y-1.5" data-testid={testId}>
    {items.map((w, i) => (
      <div key={i} className="flex items-start gap-2 rounded-md border border-structure bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
        <Info className="h-4 w-4 shrink-0 mt-0.5" />
        <span>{w}</span>
      </div>
    ))}
  </div>
) : null);

const TONE_CLASS = {
  dtc: 'border-autoxium-blue/50',
  warn: 'border-amber-600/60 bg-amber-600/10',
};

// Byte-by-byte trace built from flex divs (no raw <table> — the project has a known
// hydration gotcha with raw tables). Each cell: { range, hex, label, detail, tone }.
export const ByteTrace = ({ cells, title = 'Byte Breakdown', testId }) => (
  <div className="rounded-md border border-structure bg-muted/30 p-4" data-testid={testId}>
    <SectionLabel className="mb-3">{title}</SectionLabel>
    <div className="flex flex-wrap items-start gap-x-2 gap-y-4">
      {cells.map((c, i) => (
        <div key={i} className="flex flex-col items-center text-center min-w-[72px] max-w-full">
          <div className={`font-mono text-base sm:text-lg font-bold bg-background border rounded-md px-2.5 py-1.5 break-all ${TONE_CLASS[c.tone] || 'border-structure'}`} data-testid={testId ? `${testId}-cell-${i}` : undefined}>
            {c.hex}
          </div>
          <div className="text-muted-foreground text-sm leading-none my-1">&darr;</div>
          <div className="font-mono text-[11px] font-semibold uppercase tracking-wide max-w-[160px] leading-snug">{c.label}</div>
          <div className="text-[10px] text-muted-foreground">{c.range}</div>
        </div>
      ))}
    </div>
    <ul className="mt-4 space-y-1.5 text-xs text-muted-foreground border-t border-structure pt-3">
      {cells.map((c, i) => (
        <li key={i} className="break-words"><span className="font-mono font-semibold text-foreground">{c.hex} ({c.label}):</span> {c.detail}</li>
      ))}
    </ul>
  </div>
);

export const VERDICT_TONE = {
  danger: 'border-destructive/50 bg-destructive/10 text-destructive',
  warning: 'border-amber-600/50 bg-amber-600/10 text-amber-800 dark:text-amber-400',
  ok: 'border-emerald-600/50 bg-emerald-600/10 text-emerald-800 dark:text-emerald-400',
  muted: 'border-structure bg-muted/50 text-foreground',
};
